import React, { useRef, useState } from 'react';
import { Checkbox } from 'aha-components';
import { useTranslation } from 'react-i18next';
import { isMobile } from '@/biz/tool';
import { useWebModalStore } from '@/hooks/useWebModalStore';
import BottomDrawer from '../BottomDrawer';
import Modal from '@/components/web/components/Modal';
import { InfluencerAgreement } from '@/components/campaign/influencerAgreement';
import AddressProgress from './AddressProgress';
import styles from './ModalAddress.module.less';

interface Props {
  checked: [boolean, boolean];
  onCheckedChange: (value: [boolean, boolean]) => void;
  onCancel: () => void;
  onContinue: () => void;
  hasProductSelection: boolean;
  shouldShowCreatorAgreement: boolean;
  creatorAgreementError: boolean;
  setCreatorAgreementError: (value: boolean) => void;
}

const AgreementContent: React.FC<Props> = ({
  checked,
  onCheckedChange,
  onCancel,
  onContinue,
  hasProductSelection,
  shouldShowCreatorAgreement,
  creatorAgreementError,
  setCreatorAgreementError
}) => {
  const { t } = useTranslation();
  const { isAgree, setIsAgree, isShowAgreement, setIsShowAgreement } =
    useWebModalStore();
  const [invalidIndex, setInvalidIndex] = useState<number | null>(null);
  const itemRefs = useRef<Array<HTMLDivElement | null>>([]);

  const toggle = (index: number) => {
    const next: [boolean, boolean] = [...checked];
    next[index] = !next[index];
    onCheckedChange(next);
    if (invalidIndex === index && next[index]) setInvalidIndex(null);
  };

  const handleContinue = () => {
    const firstInvalid = checked.findIndex((value) => !value);
    if (firstInvalid >= 0) {
      setInvalidIndex(firstInvalid);
      itemRefs.current[firstInvalid]?.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
      return;
    }
    if (shouldShowCreatorAgreement && !isAgree) {
      setCreatorAgreementError(true);
      return;
    }
    onContinue();
  };

  const terms = [
    t('modal.address.agreement.delivery_term', {
      defaultValue:
        'I understand that after receiving the product, I’m expected to complete the collaboration as agreed. If there is an issue with the product, I will contact the advertiser promptly to discuss the next steps.'
    }),
    t('modal.address.agreement.content_term', {
      defaultValue:
        'I understand that I’m expected to deliver the agreed content on time. If I’m unable to complete the collaboration, the advertiser may request reimbursement for the value of the product or ask for the product to be returned. If a return is required, I will be responsible for the return shipping costs.'
    })
  ];

  return (
    <div className={isMobile() ? styles.mobileContent : styles.content}>
      <div className={`${styles.scrollBody} pb-2`}>
        <AddressProgress step={1} hasProductSelection={hasProductSelection} />
        <div className="mb-1 text-base font-semibold leading-6 text-[#171717]">
          {t('modal.address.agreement.title', {
            defaultValue: 'Agree to collaboration terms'
          })}
        </div>
        <div className="mb-5 text-sm font-normal leading-5 text-[#60646C]">
          {t('modal.address.agreement.description', {
            defaultValue:
              'Please review and agree to the terms below before entering your shipping address.'
          })}
        </div>
        <div className="flex flex-col gap-4 rounded-lg bg-[#FFFBEB] px-4 py-3">
          {terms.map((term, index) => (
            <div
              key={term}
              ref={(element) => {
                itemRefs.current[index] = element;
              }}
              className="relative flex items-start gap-2"
            >
              {invalidIndex === index && (
                <div className="absolute bottom-[calc(100%+8px)] left-0 z-10 whitespace-nowrap rounded bg-[#171717] px-3 py-2 text-xs text-white shadow-lg after:absolute after:left-3 after:top-full after:border-4 after:border-transparent after:border-t-[#171717]">
                  {t('modal.address.agreement.check_error', {
                    defaultValue: 'Please check the box to proceed'
                  })}
                </div>
              )}
              <span className="flex-shrink-0">
                <Checkbox
                  checked={checked[index]}
                  isFocused={false}
                  onChange={() => toggle(index)}
                />
              </span>
              <button
                type="button"
                className="flex-1 cursor-pointer border-0 bg-transparent p-0 text-left text-sm font-normal leading-5 text-[#44403C]"
                onClick={() => toggle(index)}
              >
                {term}
              </button>
            </div>
          ))}
          {shouldShowCreatorAgreement && (
            <div className="flex items-start gap-2 text-sm font-normal leading-5 text-[#44403C]">
              <span className="flex-shrink-0">
                <Checkbox
                  checked={isAgree}
                  isFocused={false}
                  onChange={() => {
                    setCreatorAgreementError(isAgree);
                    setIsAgree(!isAgree);
                  }}
                />
              </span>
              <div className="flex-1 text-sm font-normal leading-5 text-[#44403C]">
                {t('interest.agreement.consent_prefix', {
                  defaultValue: "I consent to AhaCreator's"
                })}{' '}
                <span
                  className="cursor-pointer text-[#0C67E6] underline"
                  onClick={() => setIsShowAgreement(!isShowAgreement)}
                >
                  {t('interest.agreement.creator_agreement', {
                    defaultValue: 'Creator Agreement'
                  })}
                </span>
                .
                {creatorAgreementError && (
                  <div className="mt-1 text-sm font-normal leading-5 text-[#df1b42]">
                    {t('interest.agreement.creator_agreement_error', {
                      defaultValue: 'Creator Agreement is uncheck'
                    })}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
        {shouldShowCreatorAgreement && (
          <>
            {isMobile() ? (
              <BottomDrawer
                visible={isShowAgreement}
                isShowFooter={false}
                isShowHeader={false}
                rootStyle={{ zIndex: 9999 }}
              >
                <InfluencerAgreement setIsShowAgreement={setIsShowAgreement} />
              </BottomDrawer>
            ) : (
              <Modal
                visible={isShowAgreement}
                isShowFooter={false}
                isShowHeader={false}
              >
                <InfluencerAgreement setIsShowAgreement={setIsShowAgreement} />
              </Modal>
            )}
          </>
        )}
      </div>
      <div className={`${styles.actionFooter} flex items-center gap-2`}>
        <button
          type="button"
          className="h-10 flex-1 cursor-pointer rounded-lg border border-solid border-[#E7E5E4] bg-white text-sm font-medium text-[#292524]"
          onClick={onCancel}
        >
          {t('modal.address.cancel', { defaultValue: 'Cancel' })}
        </button>
        <button
          type="button"
          className="h-10 flex-1 cursor-pointer rounded-lg border-0 bg-theme-color text-sm font-medium text-white"
          onClick={handleContinue}
        >
          {t('modal.address.continue', { defaultValue: 'Continue' })}
        </button>
      </div>
    </div>
  );
};

export default AgreementContent;
